import { Component } from "../lib/component.ts";

interface BoundedFieldRefs {
  counter?: HTMLElement;
}

class BoundedField extends Component<BoundedFieldRefs> {
  #field: HTMLTextAreaElement | HTMLInputElement | null = null;

  /** Original padding-inline-end of the field before the counter was added. */
  #paddingEnd = 0;

  #borderBlock = 0;
  #lineHeight = 0;
  #paddingBottom = 0;
  #maxHeight = Infinity;

  connectedCallback(): void {
    super.connectedCallback();

    this.#field = this.querySelector("textarea") ?? this.querySelector("input");
    if (!this.#field) return;

    const styles = getComputedStyle(this.#field);

    if (this.#field.maxLength > 0 && this.refs.counter) {
      this.#paddingEnd = parseFloat(styles.paddingRight) || 0;
      this.refs.counter.style.insetInlineEnd = `${this.#paddingEnd}px`;

      if (this.#field instanceof HTMLTextAreaElement) {
        this.refs.counter.style.lineHeight = styles.lineHeight;
      }
    }

    if (this.#field instanceof HTMLTextAreaElement) {
      this.#borderBlock = parseFloat(styles.borderTopWidth) + parseFloat(styles.borderBottomWidth);
      this.#lineHeight = parseFloat(styles.lineHeight);
      this.#paddingBottom = parseFloat(styles.paddingBottom);
      this.#maxHeight = parseFloat(styles.maxHeight) || Infinity;
    }

    this.#field.addEventListener("input", this.#handleInput, {
      signal: this.signal,
    });
    this.#field.addEventListener("field:refresh", this.#handleInput, {
      signal: this.signal,
    });

    this.#handleInput();
  }

  #handleInput = (): void => {
    if (!this.#field) return;

    if (this.#field.maxLength > 0 && this.refs.counter) {
      this.refs.counter.textContent = `${this.#field.value.length}/${this.#field.maxLength}`;

      const gap = 8;
      this.#field.style.paddingInlineEnd = `${this.#paddingEnd + this.refs.counter.offsetWidth + gap}px`;
    }

    if (this.#field instanceof HTMLTextAreaElement) {
      this.#field.style.overflowY = "hidden";
      this.#field.style.height = "auto";
      const naturalHeight = this.#field.scrollHeight + this.#borderBlock;
      const clamped = Math.min(naturalHeight, this.#maxHeight);
      this.#field.style.height = `${clamped}px`;
      if (naturalHeight > this.#maxHeight) {
        this.#field.style.overflowY = "auto";
      }

      if (this.#field.maxLength > 0 && this.refs.counter) {
        const counterTop = clamped - this.#paddingBottom - this.#lineHeight;
        this.refs.counter.style.top = `${counterTop}px`;
      }
    }
  };
}

if (!customElements.get("bounded-field")) {
  customElements.define("bounded-field", BoundedField);
}
